import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Camera, Lock, Scale, ArrowRight } from "lucide-react";

const PANELS = [
  { icon: Camera, text: "Photograph each room — AI tells you what you missed." },
  { icon: Lock, text: "Lock your report — timestamped evidence your landlord can view via link." },
  { icon: Scale, text: "At move-out, AI compares photos and writes your dispute letter." },
];

export default function Welcome() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const done = () => navigate("/dashboard");
  const next = () => (step < PANELS.length - 1 ? setStep(step + 1) : done());

  const Panel = PANELS[step];
  const Icon = Panel.icon;

  return (
    <div className="min-h-screen bg-navy text-white flex flex-col relative overflow-hidden dd-grain">
      <div className="relative flex items-center justify-between px-5 h-16">
        <Logo dark />
        <button data-testid="welcome-skip-btn" onClick={done} className="text-sm font-medium text-white/70 hover:text-white transition-colors">
          Skip
        </button>
      </div>

      <div className="relative flex-1 flex flex-col items-center justify-center px-6 text-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.35 }}
            className="max-w-md"
            data-testid={`welcome-panel-${step}`}
          >
            <span className="mx-auto grid place-items-center h-20 w-20 rounded-3xl bg-amber/15 text-amber">
              <Icon size={38} />
            </span>
            <p className="mt-8 font-heading text-2xl sm:text-3xl font-semibold leading-snug">{Panel.text}</p>
          </motion.div>
        </AnimatePresence>

        <div className="mt-10 flex items-center gap-2" data-testid="welcome-dots">
          {PANELS.map((_, i) => (
            <span key={i} className={`h-2 rounded-full transition-all ${i === step ? "w-6 bg-amber" : "w-2 bg-white/25"}`} />
          ))}
        </div>

        <Button
          data-testid="welcome-next-btn"
          onClick={next}
          className="mt-8 h-13 px-8 py-3.5 rounded-xl bg-amber hover:bg-amber-hover text-navy font-semibold"
        >
          {step < PANELS.length - 1 ? "Next" : "Get started"} <ArrowRight size={18} className="ml-2" />
        </Button>
      </div>
    </div>
  );
}
